import { ImgID } from "../../../image_loader/img_id.js";
import { Vector2 } from "../../../common/vector2.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformCircularMove } from "../../../transforms/transform_circular_move.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { Transformable } from "../../../transforms/transformable.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { TournamentTitle } from "../../dialog/tournament_title.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

/**
 * Key Hunt rules, built like DailyTasksOverlay: the standard overlay dim over the
 * Key Hunt window, the callouts popping in one by one, the arrows swinging out of
 * the block they point from, any tap closes. Laid out in the same centred
 * 1080x1920 frame as KeyHuntDialog, so the callouts land on the window's blocks.
 */
export class KeyHuntInfoOverlay extends AbstractOverlay
{
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1920;
  private static readonly STAGGER_MS = 56;
  private static readonly BASE_DELAY_MS = 160;
  private static readonly ITEM_ANIM_DURATION_MS = 256;
  private static readonly ARROW_ANIM_DURATION_MS = 280;
  private static readonly TITLE_ANIM_DURATION_MS = 320;

  private readonly content_holder: UIElementHolder;

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: unknown,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    const holder_w = LayoutScaler.widthFromDesign( KeyHuntInfoOverlay.DESIGN_W );
    const holder_h = LayoutScaler.heightFromDesign( KeyHuntInfoOverlay.DESIGN_H );
    this.content_holder = new UIElementHolder(
      this.ctx,
      Math.round( this.rect.getW2() - holder_w / 2 ),
      Math.round( this.rect.getH2() - holder_h / 2 ),
      holder_w,
      holder_h
    );
    this.addChild( this.content_holder );

    this.createElements();
  }

  private createElements(): void
  {
    const base_now = performance.now() + KeyHuntInfoOverlay.BASE_DELAY_MS;
    const stagger = KeyHuntInfoOverlay.STAGGER_MS;

    const addAppearTransform = ( element: Transformable, start_time: number ): void =>
    {
      element.setOpacity( 0 );
      element.setScale( new Vector2( 0.6, 0.6 ) );
      element.addTransform( new TransformOpacity( element, true, 0, 1, start_time, KeyHuntInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
      element.addTransform( new TransformScale( element, true, null, new Vector2( 1, 1 ), start_time, KeyHuntInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    };

    this.createArrowTop( base_now + stagger * 4 );
    this.createArrowBottom( base_now + stagger * 8 );
    this.createTopCallout( base_now, stagger, addAppearTransform );
    this.createCenterCallout( base_now, stagger, addAppearTransform );
    this.createBottomCallout( base_now, stagger, addAppearTransform );
    this.createTitle();
    this.createCloseButton( base_now + stagger * 9 );
  }

  private static arcParams(
    from: Vector2,
    to: Vector2,
    bulge: number
  ): { center: Vector2; radius: number; start_angle: number; end_angle: number }
  {
    const mid = new Vector2( ( from.x + to.x ) / 2, ( from.y + to.y ) / 2 );
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.sqrt( dx * dx + dy * dy );
    const perp_x = -dy / len;
    const perp_y = dx / len;
    const center = new Vector2( mid.x + perp_x * bulge, mid.y + perp_y * bulge );
    const radius = Math.sqrt( ( from.x - center.x ) ** 2 + ( from.y - center.y ) ** 2 );
    const start_angle = Math.atan2( from.y - center.y, from.x - center.x );
    let diff = Math.atan2( to.y - center.y, to.x - center.x ) - start_angle;
    while ( diff > Math.PI ) diff -= 2 * Math.PI;
    while ( diff < -Math.PI ) diff += 2 * Math.PI;
    const end_angle = start_angle + diff;
    return { center, radius, start_angle, end_angle };
  }

  /** The window's "Key Hunt" lettering without the ribbon, in the same box, rising like the other info titles. */
  private createTitle(): void
  {
    const title_x = LayoutScaler.xFromDesign( 174 );
    const title_y = LayoutScaler.yFromDesign( 121 );
    const title_offset_y = LayoutScaler.yFromDesign( 20 );
    const now = performance.now();

    const layers = TournamentTitle.createTitleLayers(
      this.ctx,
      title_x,
      title_y + title_offset_y,
      {
        localization_key: "KEY_HUNT.TITLE",
        w: 740,
        h: 150,
        font_size: 100,
      }
    );

    for ( const layer of layers )
    {
      layer.setOpacity( 0 );
      layer.addTransform( new TransformOpacity( layer, true, 0, 1, now, KeyHuntInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
      layer.addTransform( new TransformStraightMove( layer, true, null, new Vector2( title_x, title_y ), now, KeyHuntInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
      this.content_holder.addChild( layer );
    }
  }

  private createTopCallout(
    base_now: number,
    stagger: number,
    addAppearTransform: ( element: Transformable, start_time: number ) => void
  ): void
  {
    const tasks = this.addImage( ImgID.UI_KeyHuntInfoTasks, 604, 335, 427, 282 );
    const text = this.addText( "KEY_HUNT.INFO.TASKS", 109, 466, 480, 130, MyTextAlign.Left );

    addAppearTransform( tasks, base_now );
    addAppearTransform( text, base_now + stagger );
  }

  private createCenterCallout(
    base_now: number,
    stagger: number,
    addAppearTransform: ( element: Transformable, start_time: number ) => void
  ): void
  {
    const prize = this.addImage( ImgID.UI_KeyHuntInfoPrize, 117, 714, 306, 289 );
    const text = this.addText( "KEY_HUNT.INFO.CHOOSE", 508, 804, 520, 90, MyTextAlign.Left );

    addAppearTransform( prize, base_now + stagger * 3 );
    addAppearTransform( text, base_now + stagger * 4 );
  }

  private createBottomCallout(
    base_now: number,
    stagger: number,
    addAppearTransform: ( element: Transformable, start_time: number ) => void
  ): void
  {
    const safe = this.addImage( ImgID.UI_KeyHuntInfoSafe, 237, 1140, 664, 315 );
    const text = this.addText( "KEY_HUNT.INFO.REWARDS", 120, 1458, 840, 90, MyTextAlign.Center );

    addAppearTransform( safe, base_now + stagger * 6 );
    addAppearTransform( text, base_now + stagger * 7 );
  }

  /** From the tasks picture down-left to the prize; the shared arrow bitmap mirrored. */
  private createArrowTop( start_time: number ): void
  {
    const arrow_x = LayoutScaler.xFromDesign( 600 );
    const arrow_y = LayoutScaler.yFromDesign( 620 );
    const arrow_w = LayoutScaler.widthFromDesign( 168 );
    const arrow_h = LayoutScaler.heightFromDesign( 154 );
    const arrow_start_x = LayoutScaler.xFromDesign( 734 );
    const arrow_start_y = LayoutScaler.yFromDesign( 399 );
    const rotation_from = -42 * Math.PI / 180;
    const rotation_to = 0;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow,
      { direction_x: -1, direction_y: 1 }
    );
    arrow.enableRotation();
    arrow.setOpacity( 0 );
    arrow.setRotation( rotation_from );

    const arc = KeyHuntInfoOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( 120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, KeyHuntInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, rotation_from, rotation_to, start_time, KeyHuntInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc.center, arc.radius, start_time, KeyHuntInfoOverlay.ARROW_ANIM_DURATION_MS, arc.start_angle, arc.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  /** From the prize picture down-right to the safe. */
  private createArrowBottom( start_time: number ): void
  {
    const arrow_x = LayoutScaler.xFromDesign( 256 );
    const arrow_y = LayoutScaler.yFromDesign( 1042 );
    const arrow_w = LayoutScaler.widthFromDesign( 168 );
    const arrow_h = LayoutScaler.heightFromDesign( 154 );
    const arrow_start_x = LayoutScaler.xFromDesign( 186 );
    const arrow_start_y = LayoutScaler.yFromDesign( 782 );
    const rotation_from = 42 * Math.PI / 180;
    const rotation_to = 0;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow
    );
    arrow.enableRotation();
    arrow.setOpacity( 0 );
    arrow.setRotation( rotation_from );

    const arc = KeyHuntInfoOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( -120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, KeyHuntInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, rotation_from, rotation_to, start_time, KeyHuntInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc.center, arc.radius, start_time, KeyHuntInfoOverlay.ARROW_ANIM_DURATION_MS, arc.start_angle, arc.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createCloseButton( start_time: number ): void
  {
    const close = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 490 ),
      LayoutScaler.yFromDesign( 1650 ),
      LayoutScaler.widthFromDesign( 104 ),
      LayoutScaler.heightFromDesign( 104 ),
      ImgID.IconOverlayClose,
      undefined,
      true
    );
    close.setOpacity( 0 );
    close.addTransform( new TransformOpacity( close, true, 0, 1, start_time, KeyHuntInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    close.setOnClick( () => OverlayManager.closeOverlay() );
    this.content_holder.addChild( close );
  }

  private addText(
    localization_key: string,
    x: number,
    y: number,
    width: number,
    height: number,
    align: MyTextAlign
  ): UITextElement
  {
    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( height ),
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: align,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.0,
      }
    );
    this.content_holder.addChild( text );
    return text;
  }

  private addImage( img_id: ImgID, x: number, y: number, width: number, height: number ): UIImage
  {
    const image = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( height ),
      img_id
    );
    this.content_holder.addChild( image );
    return image;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public override onTouchStart( p: Vector2, now: number ): boolean
  {
    super.onTouchStart( p, now );
    OverlayManager.closeOverlay();
    return true;
  }

  public name(): string
  {
    return "KeyHuntInfoOverlay";
  }
}
